import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessor } from '@ngneat/reactive-forms';

export interface Checkbox {
  id: string;
  label: string;
  tooltip?: string;
  checked?: boolean;
}
@Component({
  selector: 'lib-vinnova-checkboxes',
  templateUrl: './checkboxes.component.html',
  styleUrls: ['./checkboxes.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxesComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckboxesComponent implements ControlValueAccessor {
  private readonly focusVisible = 'focus-visible';

  inputGroupRef = viewChild<ElementRef>('inputGroup');
  checkboxObjects = input<Checkbox[]>();
  horizontal = input<boolean>();
  error = input<boolean>();
  singleCheckbox = input<boolean>(false);
  required = input<boolean>();
  minNumberOfCheckedOptions = input<number>();
  labelPrefix = input<string>('');
  disabled = input<boolean>();
  // Large is default size.
  size = input<'small' | 'medium'>();
  labelledBy = input<string>();
  extendedDisabling = input<boolean>(false);
  showChangeIndicatorForIds = input<string[]>([]);

  mousedown: boolean;

  idsArray = signal<string[]>([]);
  requiredSignal = signal<boolean>(this.required());
  disabledSignal = signal<boolean>(this.disabled());

  constructor(
    public elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  writeValue(value): void {
    if (this.singleCheckbox()) {
      this.idsArray.set(value ? [this.checkboxObjects()[0]?.id] : []);
    } else {
      this.idsArray.set(JSON.parse(value) ?? []);
    }
    this.requiredSignal.set(
      this.idsArray().length < this.minNumberOfCheckedOptions(),
    );

    this.cdr.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: string | boolean) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  private updateCheckboxValue(id: string, fromEvent = false) {
    const checkbox = this.getCheckboxById('id-' + this.labelPrefix() + id);
    if (checkbox !== null && !checkbox.disabled) {
      if (!fromEvent) {
        checkbox.checked = !checkbox.checked;
      }
      checkbox.classList.add(this.focusVisible);
      if (checkbox.checked) {
        this.idsArray().push(id);
      } else {
        this.idsArray.set(this.idsArray().filter((x) => x !== id));
      }
      if (this.singleCheckbox() && this.checkboxObjects().length === 1) {
        this.onChange(checkbox.checked);
      } else {
        this.onChange(JSON.stringify(this.idsArray()));
      }
      this.requiredSignal.set(
        this.idsArray().length < this.minNumberOfCheckedOptions(),
      );
    }
  }

  updateValue($event) {
    this.updateCheckboxValue($event.target.dataset.value, true);
  }

  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabledSignal.set(disabled);
  }

  checkAndReturnIndex(index: number) {
    if (index === 0) {
      return 0;
    }
    return -1;
  }

  @HostListener('mousedown', ['$event'])
  onMousedown(event) {
    const input = document.activeElement;
    const label = document.activeElement.parentElement.querySelector('label');
    if (input === event.target || label === event.target) {
      this.removeClassFocusVisible(document.activeElement);
    }
    this.mousedown = true;
  }

  onFocus(event) {
    if (!this.mousedown) {
      event.target.classList.add(this.focusVisible);
    }
    this.mousedown = false;
  }

  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onKeyEnter(event) {
    if (document.activeElement.classList.contains('checkbox-tooltip')) {
      return;
    } else {
      event.preventDefault();
      const checkbox = this.getCheckboxById(document.activeElement.id);

      if (checkbox !== null) {
        checkbox.checked = !checkbox.checked;
        checkbox.classList.add(this.focusVisible);
        this.updateValue(event);
      }
    }
  }

  @HostListener('keydown.arrowright', ['$event'])
  onKeyNextTooltip(event) {
    if (document.activeElement.classList.contains('checkbox-tooltip')) {
      this.onKeyNext(event);
    } else if (
      document.activeElement.parentElement.querySelector('.checkbox-tooltip')
    ) {
      const tooltip = document.activeElement.parentElement.querySelector(
        '.checkbox-tooltip',
      ) as HTMLElement;
      tooltip.focus();
      tooltip.classList.add(this.focusVisible);
    } else {
      this.onKeyNext(event);
    }
  }

  @HostListener('keydown.arrowleft', ['$event'])
  onKeyPrevCheckbox(event) {
    if (document.activeElement.classList.contains('checkbox-tooltip')) {
      this.onKeyPrev(event);
    } else if (
      document.activeElement.parentElement.querySelector('.checkbox-tooltip')
    ) {
      const tooltip =
        document.activeElement.parentElement.previousSibling?.parentElement.querySelector(
          '.checkbox-tooltip',
        ) as HTMLElement;
      if (tooltip) {
        tooltip.focus();
        tooltip.classList.add(this.focusVisible);
      }
    } else {
      this.onKeyPrev(event);
    }
  }

  @HostListener('keydown.arrowdown', ['$event'])
  onKeyNext(event) {
    event.preventDefault();
    const nextSibling = document.activeElement.classList.contains(
      'checkbox-tooltip',
    )
      ? document.activeElement.parentElement.parentElement.nextSibling
      : document.activeElement.parentElement.nextSibling;

    this.onArrowUpOrDown(nextSibling);
  }

  @HostListener('keydown.arrowup', ['$event'])
  onKeyPrev(event) {
    event.preventDefault();
    const previousSibling = document.activeElement.classList.contains(
      'checkbox-tooltip',
    )
      ? document.activeElement.parentElement.parentElement
      : document.activeElement.parentElement.previousSibling;

    this.onArrowUpOrDown(previousSibling);
  }

  onBlur($event) {
    $event.target.classList.remove(this.focusVisible);
    this.onTouched();
  }

  onArrowUpOrDown(sibling: ChildNode) {
    if (sibling != undefined) {
      const checkbox = (sibling as HTMLElement).querySelector(
        'input',
      ) as HTMLElement;
      if (this.activeElementCheck(checkbox)) {
        checkbox.focus();
        checkbox.classList.add(this.focusVisible);
      }
    }
  }

  removeClassFocusVisible(activeElement: Element) {
    if (this.activeElementCheck(activeElement)) {
      this.getCheckboxById(activeElement.id).classList.remove(
        this.focusVisible,
      );
    }
  }

  activeElementCheck(activeElement: Element): boolean {
    return (
      activeElement?.id?.includes('id') &&
      this.inputGroupRef().nativeElement.contains(activeElement)
    );
  }

  getCheckboxById(id: string) {
    return this.inputGroupRef().nativeElement.querySelector(`#${id}`);
  }
}
